import {
  ConsentManagerTranslations,
  PurposeTranslations,
  useConsentManager,
  VendorData,
  VendorTranslations
} from "@pervanovo/bjelin-consent-manager";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useLocSettings } from "./SettingsHooks";

type CockpitVendorData = {
  _id: string;
  id: string;
  name: string;
  description: string;
  cookie_link: string;
  privacy_link: string;
  purposes: string[];
  special_purposes: string[];
  legitimate_interest_purposes: string[];
  version: number;
};

export function useConsentVendors(): Record<string, VendorData> {
  const settings = useLocSettings();
  return useMemo(() => {
    console.log("calculating useConsentVendors...");
    const result: Record<string, VendorData> = {};
    (settings.consent_vendors as CockpitVendorData[]).forEach(vendor => {
      result[vendor.id] = {
        id: vendor.id,
        purposes: vendor.purposes || [],
        specialPurposes: vendor.special_purposes || [],
        legInterestPurposes: vendor.legitimate_interest_purposes || [],
        version: vendor.version
      };
    });
    return result;
  }, [settings]);
}

export function useConsentVendorTranslations(): VendorTranslations {
  const settings = useLocSettings();
  const result: VendorTranslations = {};
  (settings.consent_vendors as CockpitVendorData[]).forEach(vendor => {
    result[vendor.id] = {
      name: vendor.name,
      description: vendor.description,
      privacyPolicyUrl: vendor.privacy_link,
      cookiePolicyUrl: vendor.cookie_link
    };
  });
  return result;
}

export function useConsentManagerTranslations(): {
  translations: ConsentManagerTranslations;
  purposeTranslations: PurposeTranslations;
  specialPurposeTranslations: PurposeTranslations;
  vendorTranslations: VendorTranslations;
} {
  const { availablePurposes, specialPurposes, availableLIPurposes } =
    useConsentManager();
  const vendorTranslations = useConsentVendorTranslations();
  const t = useTranslation("consent-manager", { useSuspense: true }).t;
  const tCM = useCallback(
    (key: string) => {
      const result = t(key, { defaultValue: "" });
      if (!result) {
        console.error(`Missing consent-manager translation: ${key}`);
      }
      return result || key;
    },
    [t]
  );
  return useMemo(() => {
    console.debug("Creating consent manager translations...");
    const translations: ConsentManagerTranslations = {
      header: tCM("header"),
      description: tCM("description"),
      acceptAll: tCM("acceptAll"),
      deny: tCM("deny"),
      showAdvanced: tCM("showAdvanced"),
      hideAdvanced: tCM("hideAdvanced"),
      close: tCM("close"),
      savePreferences: tCM("savePreferences"),
      advancedInfo: tCM("advancedInfo"),
      purposes: tCM("purposes"),
      vendors: tCM("vendors"),
      legInterest: tCM("legInterest"),
      required: tCM("required"),
      privacyPolicyUrl: tCM("privacyPolicyUrl"),
      cookiePolicyUrl: tCM("cookiePolicyUrl")
    };
    const purposeTranslations: PurposeTranslations = {};
    availablePurposes.forEach(
      purpose =>
        (purposeTranslations[purpose] = {
          name: tCM(`purpose.${purpose}.name`),
          description: tCM(`purpose.${purpose}.description`)
        })
    );
    availableLIPurposes.forEach(
      purpose =>
        (purposeTranslations[purpose] = {
          name: tCM(`purpose.${purpose}.name`),
          description: tCM(`purpose.${purpose}.description`)
        })
    );
    const specialPurposeTranslations: PurposeTranslations = {};
    specialPurposes.forEach(
      specialPurpose =>
        (specialPurposeTranslations[specialPurpose] = {
          name: tCM(`purpose.${specialPurpose}.name`),
          description: tCM(`purpose.${specialPurpose}.description`)
        })
    );
    return {
      translations,
      purposeTranslations,
      specialPurposeTranslations,
      vendorTranslations
    };
  }, [tCM, availablePurposes, specialPurposes, vendorTranslations]);
}
